const GENERIC_MESSAGE =
  'Something went wrong with your upload. Please try again.'

const UNAUTHENTICATED_MESSAGE =
  'Your session has expired. Please refresh the page and try again.'

/** Longest response body still readable as a sentence in an error message. */
const MAX_MESSAGE_LENGTH = 200

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

/**
 * What a `tus-js-client` `DetailedError` reports about the answer the upload
 * service gave. Absent when the request never got an answer — offline, DNS,
 * TLS — the case this module deliberately makes no claim about.
 */
function readResponse(
  error: unknown,
): { status?: number; body: string } | undefined {
  if (!isRecord(error) || !isRecord(error.originalResponse)) {
    return undefined
  }

  const response = error.originalResponse

  if (
    typeof response.getStatus !== 'function' ||
    typeof response.getBody !== 'function'
  ) {
    return undefined
  }

  // Called on the response rather than through local bindings: tus-js-client's
  // response is a class instance whose methods read its own request.
  const status: unknown = response.getStatus()
  const body: unknown = response.getBody()

  return {
    status: typeof status === 'number' ? status : undefined,
    body: typeof body === 'string' ? body : '',
  }
}

/**
 * The status the upload service answered a rejected request with, or
 * `undefined` when the request never got an answer.
 */
export function getUploadServiceErrorStatus(
  error: unknown,
): number | undefined {
  return readResponse(error)?.status
}

/**
 * The upload service writes its own rejections as a plain sentence meant for
 * the person uploading. A JSON or HTML body came from somewhere else — an edge
 * proxy, a gateway — and is not worth showing.
 *
 * @see services/uploads/src/uploadValidation.ts
 */
function readServiceMessage(body: string): string | undefined {
  const message = body.trim()

  if (!message || message.length > MAX_MESSAGE_LENGTH) {
    return undefined
  }

  return message.startsWith('{') || message.startsWith('<')
    ? undefined
    : message
}

/**
 * The message to show for an upload the upload service rejected, so consumers
 * see one error surface whichever transport ran.
 *
 * Returns `undefined` for any failure that did not come back from the service,
 * leaving the aws-s3 and network paths to report themselves.
 */
export function getUploadServiceErrorMessage(
  error: unknown,
): string | undefined {
  const response = readResponse(error)

  if (!response) {
    return undefined
  }

  const { status, body } = response

  // A 401 body says only that authentication is required, which reads as a
  // dead end; what the person can do about it is refresh.
  if (status === 401) {
    return UNAUTHENTICATED_MESSAGE
  }

  // An unreadable status, or a 5xx whose body is a stack trace or a proxy page,
  // means there is no rejection worth quoting.
  if (status === undefined || status < 400 || status >= 500) {
    return GENERIC_MESSAGE
  }

  return readServiceMessage(body) ?? GENERIC_MESSAGE
}
